import classNames from 'classnames';
import React, {useMemo} from 'react';

import type {FeatureVariantAssignment} from '@udemy/graphql';
import {ContextualHeader} from '@udemy/react-browse-contextual-header';
import {CertificationModel, CertificationTopicHeader} from '@udemy/react-certification-preparation';
import {AlternateHeadline, SkeletonTitle} from '@udemy/react-discovery-units';
import type {RelatedCategoryItem} from '@udemy/react-recommendations';

import {RelatedCategoryLinks} from 'src/components/related-category-links/related-category-links.react-component';

import {
    CERTIFICATION_TOPIC_HEADER_VARIATION,
    type CertificationTopicHeaderVariation,
    PRESET_ALL,
} from '../constants';
import styles from '../topic.module.less';
import {isOrgTopicPage, isSubsTopicPage, TopicPageType} from '../types/page';
import {
    type CertificationTopicHeaderData,
    HEADER_VARIANT,
    type HeaderVariant,
    type TopicContext,
} from '../types/topic';
import {TopicCertificationBanner} from './topic-certification-banner.react-component';
import {TopicHeaderSkeleton} from './topic-header-skeleton.react-component';

export const getHeaderVariant = ({
    certification,
    certDescription,
    tertiaryDescription,
    relatedCertificationBrowseAggregators,
    browseAggregatorPageEnabled,
    numCourses,
    numExercises,
    avgRating,
    topicName,
    topicImages,
    showCertificationTopicHeaderFeature,
    isExperimentConfigsLoading,
}: Pick<
    TopicHeaderProps,
    | 'certification'
    | 'certDescription'
    | 'tertiaryDescription'
    | 'relatedCertificationBrowseAggregators'
    | 'browseAggregatorPageEnabled'
    | 'numCourses'
    | 'numExercises'
    | 'avgRating'
    | 'topicName'
    | 'topicImages'
    | 'showCertificationTopicHeaderFeature'
    | 'isExperimentConfigsLoading'
>): HeaderVariant => {
    if (certDescription) {
        if (isExperimentConfigsLoading) {
            return {type: HEADER_VARIANT.LOADING};
        }

        const certificationTopicHeader = resolveCertificationTopicHeader({
            certDescription,
            topicImages,
            showCertificationTopicHeaderFeature,
        });
        if (certificationTopicHeader) {
            return {type: HEADER_VARIANT.CERTIFICATION_PREP, ...certificationTopicHeader};
        }
    }

    if (certification) {
        return {
            type: HEADER_VARIANT.CERTIFICATION,
            certification,
            relatedCertificationBrowseAggregators,
            browseAggregatorPageEnabled,
        };
    }
    if (tertiaryDescription) {
        return {
            type: HEADER_VARIANT.CONTEXTUAL,
            description: tertiaryDescription,
            numCourses,
            numExercises,
            avgRating,
        };
    }
    return {type: HEADER_VARIANT.DEFAULT, topicName};
};

/**
 * Resolves the data needed to render the new certification topic header experiment, or returns
 * `null` when it should not be shown (variation not active, missing cert description, or missing
 * hero image).
 *
 * Note: this intentionally does NOT depend on the badge-derived `certification`. The new header is
 * gated on the topic's cert descriptor (`certDescription`) so it can be shown for topics that have
 * a descriptor but no badge class.
 */
export const resolveCertificationTopicHeader = ({
    certDescription,
    topicImages,
    showCertificationTopicHeaderFeature,
}: Pick<
    TopicHeaderProps,
    'certDescription' | 'topicImages' | 'showCertificationTopicHeaderFeature'
>): CertificationTopicHeaderData | null => {
    const variation = showCertificationTopicHeaderFeature?.configuration?.variation as
        | CertificationTopicHeaderVariation
        | undefined;

    const isCertificationTopicHeaderVariation =
        variation === CERTIFICATION_TOPIC_HEADER_VARIATION.VARIANT_A ||
        variation === CERTIFICATION_TOPIC_HEADER_VARIATION.VARIANT_B;

    if (!certDescription || !topicImages.heroImage || !isCertificationTopicHeaderVariation) {
        return null;
    }

    return {
        variation,
        topicDescription: certDescription,
        heroImage: {
            url: topicImages.heroImage.url,
            highDensityUrl: topicImages.heroImage.highDensityUrl,
        },
        badgeImage: topicImages.badgeImage
            ? {
                  url: topicImages.badgeImage.url,
                  highDensityUrl: topicImages.badgeImage.highDensityUrl,
              }
            : undefined,
    };
};

interface TopicHeaderProps {
    certification?: CertificationModel;
    certDescription?: TopicContext['certDescription'];
    tertiaryDescription: TopicContext['tertiaryDescription'];
    relatedCertificationBrowseAggregators: TopicContext['relatedCertificationBrowseAggregators'];
    browseAggregatorPageEnabled?: boolean;
    numCourses: TopicContext['numCourses'];
    numExercises: TopicContext['numExercises'];
    avgRating: TopicContext['avgRating'];
    topicName: string;
    topicTitle: string;
    topicImages: TopicContext['topic']['images'];
    presetValue: string;
    pageType: TopicPageType;
    localizedEnrollmentStat?: string;
    /**
     * Localized learner count for the certification topic hero, derived from `numEnrollments` with
     * the cert-hero threshold applied on the frontend. `undefined` when below threshold or missing.
     */
    certHeroNumLearners?: string;
    relatedCategories?: RelatedCategoryItem[];
    isMobile: boolean;
    isLoading: boolean;
    isExperimentConfigsLoading?: boolean;
    showCertificationTopicHeaderFeature?: FeatureVariantAssignment;
    hasVoucher?: boolean;
    hasPracticeExam?: boolean;
}

export const TopicHeader: React.FC<TopicHeaderProps> = ({
    certification,
    certDescription,
    tertiaryDescription,
    relatedCertificationBrowseAggregators,
    browseAggregatorPageEnabled,
    numCourses,
    numExercises,
    avgRating,
    topicName,
    topicTitle,
    topicImages,
    presetValue,
    pageType,
    localizedEnrollmentStat,
    certHeroNumLearners,
    relatedCategories,
    isMobile,
    isLoading,
    isExperimentConfigsLoading = false,
    showCertificationTopicHeaderFeature,
    hasVoucher = false,
    hasPracticeExam = false,
}) => {
    const variant = getHeaderVariant({
        certification,
        certDescription,
        tertiaryDescription,
        relatedCertificationBrowseAggregators,
        browseAggregatorPageEnabled,
        numCourses,
        numExercises,
        avgRating,
        topicName,
        topicImages,
        showCertificationTopicHeaderFeature,
        isExperimentConfigsLoading,
    });

    const primaryHeading = useMemo(() => {
        const showOnlyTopicName = isSubsTopicPage(pageType) || isOrgTopicPage(pageType);
        if (presetValue === PRESET_ALL && showOnlyTopicName) {
            return topicName;
        }
        return topicTitle;
    }, [topicName, topicTitle, presetValue, pageType]);

    switch (variant.type) {
        case HEADER_VARIANT.LOADING:
            return <TopicHeaderSkeleton />;
        case HEADER_VARIANT.CERTIFICATION_PREP:
            return (
                <CertificationTopicHeader
                    className="ud-container"
                    name={primaryHeading}
                    topicDescription={variant.topicDescription}
                    images={{
                        heroImage: variant.heroImage,
                        badgeImage: variant.badgeImage,
                    }}
                    relatedCategories={relatedCategories?.map((item) => ({
                        title: item.title,
                        url: item.url,
                    }))}
                    stats={{
                        averageRating: avgRating,
                        numLearners: certHeroNumLearners,
                        numPracticeExercises: numExercises,
                        numCourses,
                    }}
                    hasVoucher={hasVoucher}
                    hasPracticeExam={hasPracticeExam}
                    variation={variant.variation}
                />
            );
        case HEADER_VARIANT.CERTIFICATION:
            return (
                <TopicCertificationBanner
                    certification={variant.certification}
                    enrollmentStat={localizedEnrollmentStat}
                    relatedCategoriesAndSubcategories={relatedCategories}
                    className="ud-container"
                    isMobile={isMobile}
                    relatedCertificationBrowseAggregators={
                        variant.relatedCertificationBrowseAggregators
                    }
                    browseAggregatorPageEnabled={variant.browseAggregatorPageEnabled}
                />
            );
        case HEADER_VARIANT.CONTEXTUAL:
            return (
                <ContextualHeader
                    className="ud-container"
                    title={primaryHeading}
                    description={variant.description}
                    enrollmentStat={localizedEnrollmentStat ?? ''}
                    numCourses={variant.numCourses}
                    numExercises={variant.numExercises}
                    rating={variant.avgRating}
                    relatedCategoriesAndSubcategories={relatedCategories}
                />
            );
        case HEADER_VARIANT.DEFAULT:
        default:
            return (
                <div
                    className={classNames(
                        'ud-container',
                        'ud-page-wrapper',
                        styles['default-header'],
                    )}
                    data-purpose="topic-default-header"
                >
                    {isLoading ? (
                        /**
                         * The alternate headline in the loading state is for the seo purposes.
                         * We're only serving our pages to bots if we're in the mx and not logged in. But title is changing for below
                         * user-product types:
                         * - Logged out - MX
                         * - Logged in - Subs
                         * - Logged in - UFB
                         *
                         * But since we're using same page for those 3 pairs, we need to wait to load the title. But as you can see
                         * we only serve Logged out - MX pair to the bots so actually there's only one title that bots can see.
                         *
                         * That's why, while loading, we're just injecting a screen reader only title for cover the waiting for getting
                         * the subscription or ud me data in order to render correct title.
                         */
                        <>
                            <SkeletonTitle
                                className={styles['title-skeleton']}
                                data-item-index={0}
                            />
                            <AlternateHeadline
                                title={primaryHeading}
                                className="ud-sr-only"
                                titleTag="h1"
                            />
                        </>
                    ) : (
                        <>
                            <AlternateHeadline
                                title={primaryHeading}
                                titleTag="h1"
                                titleClass={
                                    isMobile ? 'ud-heading-serif-xl' : 'ud-heading-serif-xxl'
                                }
                                titleStyle={isMobile ? 'title-compact' : 'title'}
                                layoutVariant={isMobile ? 'default' : 'large'}
                            />
                            <RelatedCategoryLinks
                                className={
                                    localizedEnrollmentStat
                                        ? styles['related-categories-margin-sm']
                                        : styles['related-categories-margin-lg']
                                }
                                isMobile={isMobile}
                                relatedCategoriesAndSubcategories={relatedCategories}
                                topicTitle={variant.topicName}
                            />
                        </>
                    )}
                </div>
            );
    }
};
